import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  FormControlLabel,
  Radio,
  TextField,
  FormHelperText,
  MenuItem,
  Select,
  RadioGroup,
  InputAdornment,
} from "@mui/material";
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled } from "@mui/system";
import Button from "@mui/material/Button";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { editUserCertificate, getUserDetails, logoutClear, updateUserCertificate } from "../../redux/actions/appActions";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CssSelectTextField } from "../common/CssTextField";
import moment from "moment";
import dayjs from "dayjs";
import { ROUTE_COMPLETE_PROFILE, ROUTE_CRAFTING_VIDEO, ROUTE_MAIN } from "../../constants/routes";
import ProfileTabs from "../common/ProfileTabs";
import { SET_USER_CERTIFICATE } from "../../redux/actions/actionTypes";
import { RootState } from "../../redux/types/reduxState";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
  backgroundColor: "rgb(255, 137, 96)",
}));

const timePeriods = ["day", "week", "month", "year"];

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Avatar1 = styled(Avatar)({
  width: "51px",
  height: "51px",
});

const JobType = styled("div")(() => ({
  display: "flex",
  justifyContent: "flex-start",
  gap: "8px",
  fontSize: "16px",
}));

const InputField = ({ control, name }: any) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          error={!!error}
          helperText={error?.message || " "}
          placeholder="Write here"
          variant="outlined"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "95px",
              boxShadow: "inset 0 0 11px 2px #dfd9d9",
              "& fieldset": {
                border: "none",
              },
              "& input": {
                padding: "12px 24px",
              },
            },
            marginTop: "10px",
          }}
        />
      )}
    />
  );
};

const StyledTextBox = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
});

const StyledTextBox1 = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
});

type FormValues = Yup.InferType<any>;

const certificateSchema = Yup.object().shape({
  name: Yup.string().required("Please enter certificate name"),
  institute: Yup.string().required("Please enter institute name"),
  minDuration: Yup.string().required("Please enter duration"),
  maxDuration: Yup.string().required("Please enter duration type"),
  certificate: Yup.string().notRequired(),
  expiryDate: Yup.mixed().notRequired(),
});

const schema = Yup.object().shape({
  certificates: Yup.array().of(certificateSchema),
});

const certificate = {
  name: "",
  institute: "",
  minDuration: "",
  maxDuration: "",
  certificate: "No",
  expiryDate: null,
  certificateId: "",
};

const defaultValues = {
  certificates: [certificate],
};

const CertificationNew = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);
  const userCertificates = useSelector((state: RootState) => state.app.userCertificates);
  const [SelectedJob, setSelectedJob] = useState("Yes");

  const Logout = () => {
    dispatch(logoutClear(()=>{
        navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const handleJobChange = (event: any) => {
    if (event.target.value === "No") {
      navigate(ROUTE_CRAFTING_VIDEO);
    }
    setSelectedJob(event.target.value);
  };

  useEffect(() => {
    dispatch(getUserDetails(SET_USER_CERTIFICATE, "certificates"));
  }, []);

  const { handleSubmit, control, setValue } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (userCertificates.length) {
      const data = userCertificates.map((el: any) => {
        const duration = el?.courseDuration?.split(" - ");
        return {
          certificateId: el?.certificateId,
          name: el?.certificateName,
          institute: el?.institute,
          minDuration: duration?.[0],
          maxDuration: duration?.[1],
          certificate: el?.certificateReceived ? "Yes" : "No",
          expiryDate: moment(el?.courseExpiry),
        };
      });

      setValue("certificates", data);
    }
  }, [userCertificates]);

  const {
    fields: certificateFields,
    append: appendCertificate,
    remove: removeCertificate,
  } = useFieldArray({
    control,
    name: "certificates",
  });

  const jobTypes = ["Yes", "No"];

  const styles = {
    button: {
      padding: "15px 50px",
      color: "#777777",
      backgroundColor: "#fef1ed",
      borderRadius: "99px",
      fontSize: "14px",
      fontWeight: "600",
      textTransform: "capitalize",
      width: "100%",
      marginTop: "20px",
      "&:hover": {
        backgroundColor: "#ffffff",
      },
    },
  };

  const onSubmit = (data: FormValues) => {
    const certificates = data?.certificates?.map((el: any) => {
      return {
        certificateId: el?.certificateId,
        certificateName: el?.name,
        institute: el?.institute,
        courseDuration: `${el?.minDuration} - ${el?.maxDuration}`,
        certificateReceived: el?.certificate === "Yes" ? true : false,
        courseExpiry: el?.expiryDate ? moment(el?.expiryDate).format("DD-MM-YYYY") : null,
      };
    });

    const isEqual = (edu1: any, edu2: any) => {
      return edu1.certificateId === edu2.certificateId;
    };

    const missingCertificates = userCertificates
      ?.filter((userEdu: any) => {
        return !certificates.some((edu: any) => isEqual(edu, userEdu));
      })
      .map((el: any) => ({
        ...el,
        deleted: true,
      }));

    const updatedCertificates = [...certificates, ...missingCertificates];
    const isEdit = location?.search?.includes("edit") || localStorage?.getItem("EDIT");
    if (userCertificates.length) {
      dispatch(
        editUserCertificate(updatedCertificates, () =>
          navigate(isEdit ? `${ROUTE_COMPLETE_PROFILE}` : ROUTE_CRAFTING_VIDEO)
        )
      );
    } else {
      dispatch(
        updateUserCertificate(certificates, () => navigate(isEdit ? `${ROUTE_COMPLETE_PROFILE}` : ROUTE_CRAFTING_VIDEO))
      );
    }
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#eeeeee" width="100%">
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <HomeIcon  />
            </InputAdornment> 
          </Box>
          <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <LogoutIcon  />
            </InputAdornment> 
          </Box>
        </Box>
      </Box>

      <ProfileTabs />
      <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" fontWeight="500" color="#757575">
        <Box display="flex" alignItems="center" padding="5px 16px">
          <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
          <StyledTextBox p={1} mt={2} ml={1}>
            <Box>
              <Typography className="custom-typography-chat">2/2</Typography>
              <Typography className="custom-typography-chat">Any certifications you would like to add?</Typography>
            </Box>
          </StyledTextBox>
        </Box>

        <Grid justifyContent="flex-end" container spacing={5} pl={3}>
          {jobTypes.map((jobType) => (
            <Grid item xs={4} key={jobType}>
              <FormControlLabel
                value={jobType}
                control={<Radio checked={SelectedJob === jobType} />}
                label={
                  <JobType>
                    <Typography className="custom-typography-chat">{jobType}</Typography>
                  </JobType>
                }
                onChange={handleJobChange}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {SelectedJob === "Yes" ? (
          <Box>
            <Box display="flex" alignItems="center" p={2} padding="5px 16px" mb={1}>
              <StyledTextBox1 p={1}>
                <Typography className="custom-typography-chat">
                  Let&apos;s start with your most recent certification course.
                </Typography>
              </StyledTextBox1>
            </Box>
            {certificateFields?.map((el, index) => {
              return (
                <Box
                  key={el?.id}
                  display="flex"
                  flexDirection="column"
                  p={2}
                  bgcolor="#ffffff"
                  sx={{ borderRadius: "20px 20px 0px 0px" }}
                >
                  <Grid container spacing={2} mt={2} paddingLeft="15px">
                    <Typography className="custom-typography">Certificate Name</Typography>
                    <InputField control={control} name={`certificates.${index}.name`} />
                    <Typography mt={2} className="custom-typography">
                      Institute
                    </Typography>
                    <InputField control={control} name={`certificates.${index}.institute`} />
                    <Typography mt={2} className="custom-typography">
                      Course Duration
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Controller
                          control={control}
                          name={`certificates.${index}.minDuration`}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <CssSelectTextField fullWidth error={!!error}>
                                <Select
                                  sx={{
                                    borderRadius: "99px",
                                    boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                    "& .MuiSelect-select": {
                                      padding: "12.3px 16px", // Proper padding
                                    },
                                    mt: 1,
                                  }}
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={field?.value}
                                  error={!!error}
                                  onChange={field.onChange}
                                  displayEmpty={true}
                                  renderValue={(value) => (value ? value : "Min Duration")}
                                >
                                  {Array.from({ length: 100 }).map((_, index) => {
                                    const year = index + 1;
                                    return (
                                      <MenuItem key={year} value={year}>
                                        {year}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </CssSelectTextField>
                              <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                                {error ? error?.message : " "}
                              </FormHelperText>
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          control={control}
                          name={`certificates.${index}.maxDuration`}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <CssSelectTextField fullWidth error={!!error}>
                                <Select
                                  sx={{
                                    borderRadius: "99px",
                                    boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                    "& .MuiSelect-select": {
                                      padding: "12.3px 16px", // Proper padding
                                    },
                                    mt: 1,
                                  }}
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={field?.value}
                                  error={!!error}
                                  onChange={field.onChange}
                                  displayEmpty={true}
                                  renderValue={(value) => (value ? value : "Max Duration")}
                                >
                                  {timePeriods.map((period, index) => (
                                    <MenuItem key={index} value={period}>
                                      {period}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </CssSelectTextField>
                              <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                                {error ? error?.message : " "}
                              </FormHelperText>
                            </>
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Typography mt={2} className="custom-typography">
                      Have you received certificate?{" "}
                    </Typography>

                    <Controller
                      control={control}
                      name={`certificates.${index}.certificate`}
                      render={({ field, fieldState: { error } }) => (
                        <Box>
                          <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            sx={{ display: "flex", gap: 5 }}
                          >
                            {jobTypes.map((jobType) => {
                              return (
                                <FormControlLabel
                                  key={jobType}
                                  value={jobType}
                                  control={<Radio />}
                                  label={
                                    <JobType>
                                      <Typography>{jobType}</Typography>
                                    </JobType>
                                  }
                                />
                              );
                            })}
                          </RadioGroup>
                          <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                            {error ? error?.message : " "}
                          </FormHelperText>
                        </Box>
                      )}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography className="custom-typography">Course Expiry Date </Typography>
                      </Grid>
                      <Grid item xs={6} display="flex" justifyContent="flex-end">
                        <Typography className="custom-typography">Optional</Typography>
                      </Grid>
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Box
                        sx={{
                          width: "100%",
                          borderRadius: "100px",
                          boxShadow: "inset 0 0 11px 2px #dfd9d9",
                          border: "none",
                          mt: 1,
                        }}
                      >
                        <Controller
                          control={control}
                          name={`certificates.${index}.expiryDate`}
                          render={({ field }) => (
                            <DatePicker
                              sx={{
                                "& input": {
                                  padding: "12px 16px",
                                },
                              }}
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) => field.onChange(date ? date.toDate() : null)}
                              // maxDate={dayjs()}
                              format="DD/MM/YYYY"
                            />
                          )}
                        />
                      </Box>
                    </LocalizationProvider>
                    <Button
                      onClick={() => removeCertificate(index)}
                      sx={styles.button}
                      variant="contained"
                      size="small"
                    >
                      <img src="/images/delete-bin-line.png" alt="" width="18px" /> Delete
                    </Button>
                  </Grid>
                </Box>
              );
            })}
            {certificateFields?.length === 1 && (
              <>
                <Box mx={2} display="flex" alignItems="center" pb={3}>
                  <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
                  <StyledTextBox p={1} mt={2} ml={1}>
                    <Box>
                      <Typography className="custom-typography-chat">1/2</Typography>
                      <Typography className="custom-typography-chat">
                        Would you like to add another certification?
                      </Typography>
                    </Box>
                  </StyledTextBox>
                </Box>
                <Box sx={{ mt: 2, textAlign: "right", mr: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "capitalize",
                      width: "75px",
                      cursor: "pointer",
                      border: "1px dashed #756DE9",
                      color: "#756DE9",
                      backgroundColor: "#fff !important",
                    }}
                    className="px-3 py-2 me-2 mb-2 roundedone"
                    onClick={() => appendCertificate(certificate)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      textTransform: "capitalize",
                      width: "75px",
                      cursor: "pointer",
                      border: "",
                      color: "",
                      backgroundColor: "#756DE9",
                    }}
                    className="px-3 py-2 me-2 mb-2 roundedone"
                    // onClick={() => setShow(false)}
                  >
                    No
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ) : null}
        <Box display="flex" flexDirection="column" p={2} bgcolor="#ffffff" sx={{ borderRadius: "20px 20px 0px 0px" }}>
          {location?.search?.includes("edit") || localStorage.getItem("EDIT") ? (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                justifyContent: "center",
                alignItems: "center",
                padding: "15px 64px",
                marginTop: "20px",
                marginBottom: "15px",
                fontSize: "16px",
                fontWeight: 600,
                color: "#ffffff",
                backgroundColor: "#756DE9",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                borderRadius: "95px",
                textTransform: "none",
              }}
            >
              Save
            </Button>
          ) : (
            <Box display="flex">
              <Button
                sx={{
                  backgroundColor: "white",
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "25%",
                  color: "#000000",
                  "&:hover ": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                variant="contained"
                size="small"
                style={{ padding: "15px 40px" }}
                className="w-100 mt-2 btn-pad"
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <Box flexGrow={1}></Box>
              {SelectedJob === "Yes" ? (
                <Button
                  type="submit"
                  sx={{
                    backgroundColor: "#756DE9",
                    borderRadius: "99px",
                    fontSize: "16px",
                    textTransform: "capitalize",
                    width: "70%",
                    paddingY: "15px",
                    paddingX: 0,
                    "&:hover": {
                      backgroundColor: "#756DE9",
                    },
                  }}
                  variant="contained"
                  size="small"
                  className="w-100 mt-2 btn-pad"
                  disabled={ajaxCallsInProgress}
                >
                  Next Up: Video
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("")}
                  sx={{
                    backgroundColor: "#756DE9",
                    borderRadius: "99px",
                    fontSize: "16px",
                    textTransform: "capitalize",
                    width: "70%",
                    paddingY: "15px",
                    paddingX: 0,
                    "&:hover": {
                      backgroundColor: "#756DE9",
                    },
                  }}
                  variant="contained"
                  size="small"
                  className="w-100 mt-2 btn-pad"
                  disabled={!!ajaxCallsInProgress}
                >
                  Next Up: Video
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CertificationNew;
